import { useParams } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { calculateFinancialSummary, 
  formatCurrency, 
  getVal, 
  setBool, 
  isNegative,
  formatDate
} from '../../helpers/process_morningstar.js';

import {
  Box,
  Container,
  CircularProgress,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableHead,
  TableCell,
  TableContainer,
  TableRow,
  Paper
} from "@mui/material";

const white_rows = [
  "",
  "grossMarginIncl", 
  "grossMarginExcl", 
  "operatingIncome", 
  "ebit", 
  "incomeAvail",
  "beforeTaxIncome",
  "netIncomeFromCO",
  "netIncomeAvailable",
  "totalCurrentAssets",
  "totalNonCurrentAssets",
  "totalAssets",
  "totalCurrentLiabilities",
  "totalNonCurrentLiabilities",
  "totalLiabilities",
  "totalOwnerEquity",
  "totalLiabilitiesAndEquity"
];

const beige_rows = [
  "totalRevenueGrw",
  "ebitda",
  "ebitdaMargin",
  "ebitdaGrw",
  "netIncome",
  "EBITDA Margin %",
  "Total Revenue Growth %"
];


export default function FinancialSummary() {
  const tableRef = useRef(null);
  const [tables, setTables] = useState([]);
  const [error, setError] = useState(null);
  const [currentTableData, setCurrentTableData] = useState([]);
  const [capRD, setCapRD] = useState(getVal("capRD"));
  const [includeOtherIncome, setIncludeOtherIncome] = useState(getVal("includeOtherIncome"));

  useEffect(() => {
    if (tableRef.current) {
      const el = tableRef.current;
      el.scrollTo({
        left: el.scrollWidth,
      });
    }
  }, [currentTableData]);

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === "capRD") setCapRD(JSON.parse(event.newValue));
      if (event.key === "includeOtherIncome") setIncludeOtherIncome(JSON.parse(event.newValue));
    };
  
    const handleCustomUpdate = () => {
      setCapRD(getVal("capRD"));
      setIncludeOtherIncome(getVal("includeOtherIncome"));
    };
  
    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("localStorageUpdated", handleCustomUpdate);
  
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("localStorageUpdated", handleCustomUpdate);
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      const financialSummaryData = await calculateFinancialSummary(capRD, includeOtherIncome, getVal("selectedTax"));
      setCurrentTableData(financialSummaryData);
    };

    fetchData();
  }, [capRD, includeOtherIncome]);

    // Toggle Cap R&D and recalculate
  const handleToggleCapRD = async () => {
    const newCapRD = !capRD;
    setCapRD(newCapRD);
    setBool("capRD", newCapRD);

    window.dispatchEvent(new Event("localStorageUpdated"));

    const updatedData = await calculateFinancialSummary(newCapRD, includeOtherIncome, getVal("selectedTax"));
    updateFinancialSummaryTable(updatedData);
    
  };

  // Toggle Include Other Income and recalculate
  const handleToggleIncludeOtherIncome = async () => {
    const newInclude = !includeOtherIncome;
    setIncludeOtherIncome(newInclude);
    setBool("includeOtherIncome", newInclude);

    window.dispatchEvent(new Event("localStorageUpdated"));

    const updatedData = await calculateFinancialSummary(capRD, newInclude, getVal("selectedTax"));
    updateFinancialSummaryTable(updatedData);
  };

  // Helper to update table data
  const updateFinancialSummaryTable = (newData) => {
    setTables(prev =>
      prev.map(t =>
        t.tableName === "Financial Summary" ? { ...t, tableData: newData } : t
      )
    );
    setCurrentTableData(newData);
  };

  if (error) return <div>Error: {error}</div>;

      return (
        <>
            <>

              {currentTableData.length > 0 ? (
              <Box sx={{ mx: "25px" }}>
               <TableContainer component={Paper} sx={{ width: '100%', overflowX: 'auto', boxShadow: "none", pb: 10, position: "relative" }} ref={tableRef}>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{
                        position: "sticky",
                        left: 0,
                      }}
                    >
                  Income Statement
                </Typography>

                <Table stickyHeader size="small">
                  <TableHead>
                      {[{ value : "Date", time: "Time" }].map((item, idx) => (
                      <TableRow>
                        <TableCell 
                          sx={{ 
                            backgroundColor: "#F9FAFB",
                            borderRight: "1px solid #969696ff", 
                            fontWeight: "bold", 
                            color: "black",
                            position: "sticky", 
                            left: 0, 
                            zIndex: 3, 
                            minWidth: 300,
                          }}
                        >
                          Date
                        </TableCell>
                        {currentTableData.map((row, colIdx) => (
                          <TableCell sx={{backgroundColor: row[item.time] === "Annual" ? "#324052" : "#FFF176", 
                              color: row[item.time] === "Annual" ? "white" : "black", 
                              borderRight: "1px solid #bbbbbbff",
                              whiteSpace: 'nowrap',
                              padding: 0,
                              minWidth: 65.53,
                            }} 
                              key={colIdx}
                          >
                            <div style={{ 
                              textAlign: "center", 
                              fontSize: "0.67rem",
                              padding: 0 
                              }}
                            >
                              {row[item.time]}
                            </div>
                            <div style={{ 
                              fontSize: "0.67rem",
                              textAlign: "center",
                              fontWeight: "bold",
                              textDecoration: "underline",
                              padding: 0
                              }}
                            >
                              {formatDate(row[item.value])}
                            </div>
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableHead>
                  <TableBody>
                    {[
                      { label: "Operating Revenue", field: "operatingRevenue" },
                      { label: "Total Revenue", field: "totalRevenue" },
                      { label: "Total Revenue Growth %", field: "totalRevenueGrw" },
                      { label: "Adjustments to Revenue", field: "otherTaxes" },
                      { label: "Cost of Sales (incl depn)", field: "costOfSales" },
                      { label: "Cost of Sales (excl depn)", field: "costOfRevenue" },
                      { label: "Gross Margin (incl depn)", field: "grossMarginIncl" },
                      { label: "Gross Margin (excl depn)", field: "grossMarginExcl" },
                      { label: "Selling General and Administration", field: "generalAndAdmin" },
                      { label: "Research and Development", field: "researchAndDevelopment" },
                      { label: "Other Income / (Expenses)", field: "otherIncome" },
                      { label: "Operating Income", field: "operatingIncome" },
                      { label: "Earnings from Equity Interest", field: "equityInterest" },
                      { label: "EBITDA", field: "ebitda" },
                      { label: "EBITDA Margin %", field: "ebitdaMargin" },
                      { label: "EBITDA Growth %", field: "ebitdaGrw" },
                      { label: "Depreciation", field: "depreciation" },
                      { label: "EBIT", field: "ebit" },
                      { label: "Interest Income", field: "interestIncome" },
                      { label: "Income / (Expense) Restructuring and M&A", field: "incomeRest" },
                      { label: "Income Acquired in Process R&D", field: "incomeAcquired" },
                      { label: "Special Income / (Charges)", field: "specialIncome" },
                      { label: "Total Income Avail for Interest Expense", field: "incomeAvail" },
                      { label: "Interest Expense", field: "interestExpense" },
                      { label: "Income Before Tax", field: "beforeTaxIncome" },
                      { label: "Income Tax", field: "incomeTax" },
                      { label: "Net Income from Continuous Operations", field: "netIncomeFromCO" },
                      { label: "Non-recurring Events", field: "" },
                      { label: "Net Income from Discontinued Operations", field: "netIncomeFromDO" },
                      { label: "Net Income from Extraordinary Items", field: "netIncomeFromEI" },
                      { label: "Net Income Effect of Accounting Changes", field: "netIncomeFromAC" },
                      { label: "Net Income from Tax Loss Carryforward", field: "netIncomeFromTLC" },
                      { label: "Net Income from Other Gains (Losses)", field: "netIncomeFromOG" },
                      { label: "Minority Interests", field: "minorityInterests" },
                      { label: "Net Income", field: "netIncome" },
                      { label: "Preferred Dividends", field: "preferredDividends" },
                      { label: "Net Income Available to Common Shareholders", field: "netIncomeAvailable" },
                      { label: "", field: "" },
                      { label: "Shares Outstanding", field: "sharesOutstanding" },
                      { label: "Basic EPS from Continuing Ops.", field: "basicEpsContinuingOps" },
                      { label: "Basic EPS from Discontinued Ops.", field: "basicEpsDiscontinuedOps" },
                      { label: "Basic EPS from Extraordinary Inc.", field: "basicEpsExtraordinaryINC" },
                      { label: "Basic EPS from Cum Effect of Accounting Changes", field: "basicEpsAccountingChg" },
                      { label: "Basic EPS from Other Gains (Losses)", field: "basicEpsOtherGains" },
                      { label: "Basic EPS Total", field: "basicEpsTotal" },
                      { label: "Basic Normalized Net Income Per Share", field: "normalizedBasicEps" },
                      { label: "Basic Average Weighted Shares", field: "basicAverageShares" },
                      { label: "Diluted EPS from Continuing Ops.", field: "dilutedEpsContinuingOps" },
                      { label: "Diluted EPS from Discontinued Ops.", field: "dilutedEpsDiscontinuedOps" },
                      { label: "Diluted EPS from Extraordinary Inc.", field: "dilutedEpsExtraordinaryINC" },
                      { label: "Diluted EPS from Cum Effect of Accounting Changes", field: "dilutedEpsAccountingChg" },
                      { label: "Diluted EPS from Other Gains (Losses)", field: "dilutedEpsOtherGains" },
                      { label: "Diluted EPS Total", field: "dilutedEpsTotal" },
                      { label: "Diluted Normalized Net Income Per Share", field: "normalizedDilutedEps" },
                      { label: "Diluted Average Weighted Shares", field: "dilutedAverageShares" },
                      { label: "Dividends Paid Per Share", field: "dividendPerShare" },
                    ].map((rowItem, rowIdx) => (
                      <TableRow
                        key={rowIdx}
                        sx={{
                          backgroundColor: white_rows.includes(rowItem.field) 
                            ? "white" 
                            : beige_rows.includes(rowItem.field) 
                            ? "#fffaed" 
                            : "#ccffcc",
                          "& td": { fontSize: "0.67rem", borderBottom: "1px solid #E5E7EB", padding: "1px 8px" },
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {/* Render the row label as first cell */}
                        <TableCell sx={{ 
                            fontWeight: 'bold' ,
                            position: "sticky", 
                            left: 0, 
                            zIndex: 2,
                            height: 15,
                            borderRight: "1px solid #969696ff",
                            backgroundColor: beige_rows.includes(rowItem.field)  
                              ? "#fffaed" 
                              : rowIdx % 2 === 0 
                              ? "#ffffff" 
                              : "#F9FAFB",
                          }}
                        >
                          {rowItem.label}
                        
                            {rowItem.field === "researchAndDevelopment" && (
                            <label style={{ fontSize: "0.67rem", cursor: "pointer", marginLeft: "4px" }}>
                              <input
                                type="checkbox"
                                checked={getVal("capRD")}
                                onChange={handleToggleCapRD}
                                style={{ 
                                  marginRight: "6px",
                                  accentColor: "black",
                                  backgroundColor: "white"
                                }}
                              />
                              Cap R&D
                            </label>
                            )}

                            {rowItem.field === "otherIncome" && (
                            <label style={{ fontSize: "0.67rem", cursor: "pointer", marginLeft: "9px" }}>
                              <input
                                type="checkbox"
                                checked={!getVal("includeOtherIncome")}
                                onChange={handleToggleIncludeOtherIncome}
                                style={{ 
                                  marginRight: "6px",
                                  accentColor: "black",
                                  backgroundColor: "white"
                                 }}
                              />
                              Exclude
                            </label>
                            )}
                        </TableCell>

                        {/* Render the financial data cells */}
                        {currentTableData.map((row, colIdx) => (
                          <TableCell key={colIdx} sx={{ 
                            textAlign: "right",
                            borderRight: "1px solid #bbbbbbff",
                            color: (() => {
                              const isGrey =
                                (rowItem.field === "researchAndDevelopment" && capRD) ||
                                (rowItem.field === "otherIncome" && !includeOtherIncome);

                              if (isGrey) {
                                return "#d9d9d9"; // always grey if toggled
                              }
                              if (isNegative(row[rowItem.field])) {
                                return "red"; // red only when not greyed out
                              }
                              return "inherit";
                            })(),
                            backgroundColor:
                              (rowItem.field === "researchAndDevelopment" && capRD) ||
                              (rowItem.field === "otherIncome" && !includeOtherIncome)
                                ? "#f0f0f0"
                                : "transparent",
                            }}
                          >
                            {formatCurrency(row[rowItem.field])}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{
                        position: "sticky",
                        left: 0,
                        mt: 1,
                        mb: 1
                      }}
                    >
                    Balance Sheet
                </Typography>

                {/* Assets */}
                <Table stickyHeader size="small">
                  <TableHead>
                      {[{ value : "Date", time: "Time" }].map((item, idx) => (
                      <TableRow>
                        <TableCell 
                          sx={{ 
                            backgroundColor: "#F9FAFB", 
                            borderRight: "1px solid #969696ff",
                            fontWeight: "bold", 
                            color: "black",
                            position: "sticky", 
                            left: 0, 
                            zIndex: 3, 
                            minWidth: 300,
                          }}
                        >
                          Assets
                        </TableCell>
                        {currentTableData.map((row, colIdx) => (
                          <TableCell sx={{backgroundColor: row[item.time] === "Annual" ? "#324052" : "#FFF176", 
                              color: row[item.time] === "Annual" ? "white" : "black", 
                              borderRight: "1px solid #bbbbbbff",
                              whiteSpace: 'nowrap',
                              padding: 0,
                              minWidth: 65.53,
                            }} 
                              key={colIdx}
                          >
                            <div style={{ 
                              textAlign: "center", 
                              fontSize: "0.67rem",
                              padding: 0
                              }}
                            >
                              {row[item.time]}
                            </div>
                            <div style={{ 
                              fontSize: "0.67rem",
                              textAlign: "center",
                              fontWeight: "bold",
                              textDecoration: "underline",
                              padding: 0,
                              }}
                            >
                              {formatDate(row[item.value])}
                            </div>
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableHead>
                  <TableBody>
                    {[
                      { label: "Current Assets", value: "" },
                      { label: "Cash and Equivalents", value: "cashAndEquivalents" },
                      { label: "Restricted Cash", value: "restrictedCash" },
                      { label: "Marketable Securities", value: "marketableSecurities" },
                      { label: "Accounts Receivable", value: "accountsReceivable" },
                      { label: "Inventory", value: "inventory" },
                      { label: "Prepaid Expenses", value: "prepaidExpenses" },
                      { label: "Current Deferred Assets", value: "currentDeferredAssets" },
                      { label: "Other Current Assets", value: "otherCurrentAssets" },
                      { label: "Total Current Assets", value: "totalCurrentAssets" },
                      { label: "", value: "" },
                      { label: "Non-Current Assets", value: "" },
                      { label: "Capitalized R&D", value: "capitalizedRD" },
                      { label: "Gross Fixed Assets (Plant, Prop, & Equipment, R&D)", value: "grossFixedAssets" },
                      { label: "Accumulated Depreciation and Depletion", value: "accumulatedDepreciation" },
                      { label: "Net Premises and Equipment", value: "netPremises" },
                      { label: "Goodwill", value: "goodwill" },
                      { label: "Other Intangible Assets", value: "otherIntangibleAssets" },
                      { label: "Non Current Deferred Assets", value: "nonCurrentDeferredAssets" },
                      { label: "Other Non-Current Assets", value: "otherNonCurrentAssets" },
                      { label: "Investments and Advances", value: "investmentsAndAdvances" },
                      { label: "Total Non-Current Assets", value: "totalNonCurrentAssets" },
                      { label: "Total Assets", value: "totalAssets" },
                    ].map((item, idx) => (
                    <TableRow key={idx}
                        sx={{
                          backgroundColor: white_rows.includes(item.value) ? "white" : "#ccffcc",
                          "& td": { fontSize: "0.67rem", borderBottom: "1px solid #E5E7EB", padding: "1px 8px" },
                          whiteSpace: 'nowrap',
                        }}
                      >
                      <TableCell sx={{ 
                        fontWeight: "bold", 
                        whiteSpace: 'nowrap',
                        position: "sticky", 
                        left: 0, 
                        zIndex: 2,  
                        height: 15,
                        borderRight: "1px solid #969696ff",
                        backgroundColor: idx % 2 === 0 ? "#ffffff" : "#F9FAFB",
                        }}
                      >
                        {item.label}

                            {item.label === "Capitalized R&D" && (
                            <label style={{ fontSize: "0.67rem", cursor: "pointer", marginLeft: "4px" }}>
                              <input
                                type="checkbox"
                                checked={capRD}
                                onChange={handleToggleCapRD}
                                style={{ 
                                  marginRight: "6px",
                                  accentColor: "black",
                                  backgroundColor: "white"
                                }}
                              />
                              Cap R&D
                            </label>
                            )}
                      </TableCell>
                      {currentTableData.map((row, colIdx) => (
                        <TableCell key={colIdx} sx={{ 
                          textAlign: "right",
                          borderRight: "1px solid #bbbbbbff",
                            color: (() => {
                              const isGrey =
                                (item.value === "capitalizedRD" && !capRD);

                              if (isGrey) {
                                return "#9e9e9e"; // always grey if toggled
                              }
                              if (isNegative(row[item.value])) {
                                return "red"; // red only when not greyed out
                              }
                              return "inherit";
                            })(),
                            backgroundColor:
                              (item.value === "capitalizedRD" && !capRD) 
                                ? "#f0f0f0"
                                : "transparent",
                        }}>
                          {formatCurrency(row[item.value])}
                        </TableCell>
                      ))}
                    </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {/* Liabilities */}
                <Table stickyHeader size="small" sx={{ mt: 3 }}>
                  <TableHead>
                      {[{ value : "Date", time: "Time" }].map((item, idx) => (
                      <TableRow>
                        <TableCell 
                          sx={{ 
                            backgroundColor: "#F9FAFB", 
                            borderRight: "1px solid #969696ff",
                            fontWeight: "bold", 
                            color: "black",
                            position: "sticky", 
                            left: 0, 
                            zIndex: 3, 
                            minWidth: 300,
                          }}
                        >
                          Liabilities
                        </TableCell>
                        {currentTableData.map((row, colIdx) => (
                          <TableCell sx={{backgroundColor: row[item.time] === "Annual" ? "#324052" : "#FFF176", 
                              color: row[item.time] === "Annual" ? "white" : "black", 
                              borderRight: "1px solid #bbbbbbff",
                              whiteSpace: 'nowrap',
                              padding: 0,
                              minWidth: 65.53,
                            }} 
                              key={colIdx}
                          >
                            <div style={{ 
                              textAlign: "center", 
                              fontSize: "0.67rem",
                              padding: 0
                              }}
                            >
                              {row[item.time]}
                            </div>
                            <div style={{ 
                              fontSize: "0.67rem",
                              textAlign: "center",
                              fontWeight: "bold",
                              textDecoration: "underline"
                              }}
                            >
                              {formatDate(row[item.value])}
                            </div>
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableHead>
                  <TableBody>
                    {[
                      { label: "Current Liabilities", value: "" },
                      { label: "Accounts Payable", value: "accountsPayable" },
                      { label: "Dividends Payable", value: "dividendsPayable" },
                      { label: "Notes Payable", value: "notesPayable" },
                      { label: "Short Term Debt", value: "shortTermDebt" },
                      { label: "Accrued Expenses", value: "accruedExpenses" },
                      { label: "Deferred Revenue", value: "deferredRevenue" },
                      { label: "Current Deferred Income Taxes", value: "currentDeferredIncomeTaxes" },
                      { label: "Other Current Liabilities", value: "otherCurrentLiabilities" },
                      { label: "Other Miscellaneous Liabilities", value: "otherMiscLiabilities" },
                      { label: "Total Current Liabilities", value: "totalCurrentLiabilities" },
                      { label: "Non-Current Liabilities", value: "nonCurrentLiabilities" },
                      { label: "Long Term Debt", value: "longTermDebt" },
                      { label: "Capital Lease Obligations", value: "capitalLeaseObligations" },
                      { label: "Deferred Income Taxes", value: "deferredIncomeTaxes" },
                      { label: "Other Deferred Tax Assets", value: "otherDeferredTaxAssets" },
                      { label: "Derivative Product Liabilities", value: "derivativeLiabilities" },
                      { label: "Pension and Post-Retirement Benefits", value: "retirementBenefits" },
                      { label: "Long-Term Provisions (Legal)", value: "longTermProvisions" },
                      { label: "Regulatory Liabilities", value: "regulatoryLiabilities" },
                      { label: "Non-Current Other Liabilities", value: "nonCurrentOtherLiabilities" },
                      { label: "Non-Current Miscellaneous Liabilities", value: "nonCurrentMiscLiabilities" },
                      { label: "Total Non-Current Liabilities", value: "totalNonCurrentLiabilities" },
                      { label: "Total Liabilities", value: "totalLiabilities" },
                    ].map((item, idx) => (
                      <TableRow key={idx}
                        sx={{
                          backgroundColor: white_rows.includes(item.value) ? "white" : "#ccffcc",
                          "& td": { fontSize: "0.67rem", borderBottom: "1px solid #E5E7EB", padding: "1px 8px" },
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <TableCell sx={{ 
                          fontWeight: "bold", 
                          whiteSpace: 'nowrap',
                          position: "sticky", 
                          left: 0, 
                          zIndex: 2,  
                          height: 15,
                          borderRight: "1px solid #969696ff",
                          backgroundColor: idx % 2 === 0 ? "#ffffff" : "#F9FAFB" 
                          }}
                        >
                          {item.label}
                        </TableCell>
                      {currentTableData.map((row, colIdx) => (
                        <TableCell key={colIdx} sx={{ 
                          borderRight: "1px solid #bbbbbbff",
                          color: isNegative(row[item.value]) ? "red" : "inherit",
                          textAlign: "right"
                        }}>
                          {formatCurrency(row[item.value])}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

                {/* Equity */}
                <Table stickyHeader size="small" sx={{ mt: 3 }}>
                  <TableHead>
                      {[{ value : "Date", time: "Time" }].map((item, idx) => (
                      <TableRow>
                        <TableCell 
                          sx={{ 
                            backgroundColor: "#F9FAFB",
                            borderRight: "1px solid #969696ff",
                            fontWeight: "bold", 
                            color: "black",
                            position: "sticky", 
                            left: 0, 
                            zIndex: 3,
                            minWidth: 300, 
                          }}
                        >
                          Owner's Equity
                        </TableCell>
                        {currentTableData.map((row, colIdx) => (
                          <TableCell sx={{backgroundColor: row[item.time] === "Annual" ? "#324052" : "#FFF176", 
                              color: row[item.time] === "Annual" ? "white" : "black", 
                              borderRight: "1px solid #bbbbbbff",
                              whiteSpace: 'nowrap',
                              padding: 0,
                              minWidth: 65.53,
                            }} 
                              key={colIdx}
                          >
                            <div style={{ 
                              textAlign: "center", 
                              fontSize: "0.67rem",
                              padding: 0
                              }}
                            >
                              {row[item.time]}
                            </div>
                            <div style={{ 
                              fontSize: "0.67rem",
                              textAlign: "center",
                              fontWeight: "bold",
                              textDecoration: "underline",
                              padding: 0
                              }}
                            >
                              {formatDate(row[item.value])}
                            </div>
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableHead>
                  <TableBody>
                    {[
                      { label: "Minority Interests", value: "minorityInterestsBal" },
                      { label: "Preferred Securities of Subsidiaries", value: "preferredSecurities" },
                      { label: "Preferred Equity Outside Stock Equity", value: "outsideStockEquity" },
                      { label: "Preferred Stock Equity", value: "stockEquity" },
                      { label: "Common Stock", value: "commonStock" },
                      { label: "Total Owner's Equity", value: "totalOwnerEquity" },
                      { label: "Total Liabilities & Owner's Equity", value: "totalLiabilitiesAndEquity" },
                      { label: "", value: "" },
                      { label: "Additional Paid In Capital", value: "additionalPaidInCapital" },
                      { label: "Retained Earnings", value: "retainedEarnings" },
                      { label: "Treasury Stock", value: "treasuryStock" },
                      { label: "Accumulated Other Comprehensive Income", value: "otherComprehensiveIncome" },
                    ].map((item, idx) => (
                      <TableRow key={idx}        
                        sx={{
                          backgroundColor: white_rows.includes(item.value) ? "white" : "#ccffcc",
                          "& td": { fontSize: "0.67rem", borderBottom: "1px solid #E5E7EB", padding: "1px 8px" },
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <TableCell sx={{ 
                          fontWeight: "bold", 
                          whiteSpace: 'nowrap',
                          position: "sticky", 
                          left: 0, 
                          zIndex: 2, 
                          height: 15, 
                          borderRight: "1px solid #969696ff",
                          backgroundColor: idx % 2 === 0 ? "#ffffff" : "#F9FAFB"
                          }}
                        >
                          {item.label}
                        </TableCell>
                        {currentTableData.map((row, colIdx) => (
                        <TableCell key={colIdx} sx={{ 
                          color: isNegative(row[item.value]) ? "red" : "inherit",
                          borderRight: "1px solid #bbbbbbff",
                          textAlign: "right"
                        }}>
                          {formatCurrency(row[item.value])}
                        </TableCell>
                      ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
            ) : (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "50vh", // full screen height
                    }}
                  >
                    <h3>Loading...</h3>
                    <CircularProgress />
                  </div>
            )}
          </>
        </>
      )}
